import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useMyClinicRole } from '@/hooks/useMyClinicRole';
import { Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Save } from 'lucide-react';

const CLINIC_TYPES = [
  'Primary Care',
  'Dental',
  'Specialty',
  'Mental Health',
  'Substance Use / Behavioral Health',
  'Multi-Specialty',
  'Urgent Care',
  'Pediatrics',
  'OB/GYN',
  'Other',
];

const TEAM_SIZES = [
  '1–10 staff',
  '11–25 staff',
  '26–50 staff',
  '51–100 staff',
  '100+ staff',
];

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada',
  'New Hampshire','New Jersey','New Mexico','New York','North Carolina',
  'North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island',
  'South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
  'Virginia','Washington','West Virginia','Wisconsin','Wyoming',
];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
];

interface ClinicProfile {
  id: string;
  name: string;
  clinic_type: string | null;
  team_size: string | null;
  state: string | null;
  contact_email: string | null;
  timezone: string;
}

export default function SettingsProfile() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { role, isLoading: roleLoading } = useMyClinicRole();
  const isReadOnly = role === 'admin';

  const [form, setForm] = useState({
    name: '',
    clinic_type: '',
    team_size: '',
    state: '',
    contact_email: '',
    timezone: 'America/New_York',
  });

  // Fetch full clinic profile
  const { data: clinic, isLoading } = useQuery({
    queryKey: ['clinic-profile', profile?.clinic_id],
    queryFn: async () => {
      if (!profile?.clinic_id) return null;
      const { data, error } = await (supabase as any)
        .from('clinics')
        .select('id, name, clinic_type, team_size, state, contact_email, timezone')
        .eq('id', profile.clinic_id)
        .maybeSingle();
      if (error) throw error;
      return data as ClinicProfile | null;
    },
    enabled: !!profile?.clinic_id,
  });

  // Populate form when data loads
  useEffect(() => {
    if (clinic) {
      setForm({
        name: clinic.name ?? '',
        clinic_type: clinic.clinic_type ?? '',
        team_size: clinic.team_size ?? '',
        state: clinic.state ?? '',
        contact_email: clinic.contact_email ?? '',
        timezone: clinic.timezone ?? 'America/New_York',
      });
    }
  }, [clinic]);

  const updateMutation = useMutation({
    mutationFn: async (values: typeof form) => {
      if (!profile?.clinic_id) throw new Error('No clinic found');
      const { error } = await (supabase as any)
        .from('clinics')
        .update({
          name: values.name,
          clinic_type: values.clinic_type || null,
          team_size: values.team_size || null,
          state: values.state || null,
          contact_email: values.contact_email || null,
          timezone: values.timezone,
        })
        .eq('id', profile.clinic_id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic', profile?.clinic_id] });
      queryClient.invalidateQueries({ queryKey: ['clinic-profile', profile?.clinic_id] });
      toast({ title: 'Clinic profile updated' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update profile',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) {
      toast({ title: 'Clinic name is required', variant: 'destructive' });
      return;
    }
    updateMutation.mutate(form);
  }

  if (isLoading || roleLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (role === 'staff') {
    return <Navigate to="/settings/my-profile" replace />;
  }

  return (
    <div>
      {/* Section Header */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-foreground">Clinic Profile</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Basic information about your clinic
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Clinic Name */}
        <div className="space-y-1.5">
          <Label htmlFor="clinic-name">
            Clinic Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="clinic-name"
            value={form.name}
            onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Riverside Family Clinic"
            disabled={isReadOnly}
            className="border-gray-200 dark:border-border bg-white dark:bg-background focus:border-primary focus:ring-primary"
          />
        </div>

        {/* Clinic Type */}
        <div className="space-y-1.5">
          <Label>Clinic Type</Label>
          <Select
            value={form.clinic_type}
            onValueChange={(v) => setForm(f => ({ ...f, clinic_type: v }))}
            disabled={isReadOnly}
          >
            <SelectTrigger className="border-gray-200 dark:border-border bg-white dark:bg-background">
              <SelectValue placeholder="Select clinic type" />
            </SelectTrigger>
            <SelectContent>
              {CLINIC_TYPES.map((type) => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Team Size */}
        <div className="space-y-1.5">
          <Label>Team Size</Label>
          <Select
            value={form.team_size}
            onValueChange={(v) => setForm(f => ({ ...f, team_size: v }))}
            disabled={isReadOnly}
          >
            <SelectTrigger className="border-gray-200 dark:border-border bg-white dark:bg-background">
              <SelectValue placeholder="Select team size" />
            </SelectTrigger>
            <SelectContent>
              {TEAM_SIZES.map((size) => (
                <SelectItem key={size} value={size}>{size}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* State */}
        <div className="space-y-1.5">
          <Label>State</Label>
          <Select
            value={form.state}
            onValueChange={(v) => setForm(f => ({ ...f, state: v }))}
            disabled={isReadOnly}
          >
            <SelectTrigger className="border-gray-200 dark:border-border bg-white dark:bg-background">
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.map((state) => (
                <SelectItem key={state} value={state}>{state}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Timezone */}
        <div className="space-y-1.5">
          <Label>Timezone</Label>
          <Select
            value={form.timezone}
            onValueChange={(v) => setForm(f => ({ ...f, timezone: v }))}
            disabled={isReadOnly}
          >
            <SelectTrigger className="border-gray-200 dark:border-border bg-white dark:bg-background">
              <SelectValue placeholder="Select timezone" />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONES.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Used to schedule notification emails at the right time for your clinic
          </p>
        </div>

        {/* Contact Email */}
        <div className="space-y-1.5">
          <Label htmlFor="contact-email">
            Primary Contact Email
          </Label>
          <Input
            id="contact-email"
            type="email"
            value={form.contact_email}
            onChange={(e) => setForm(f => ({ ...f, contact_email: e.target.value }))}
            placeholder="compliance@yourclinic.com"
            disabled={isReadOnly}
            className="border-gray-200 dark:border-border bg-white dark:bg-background focus:border-primary focus:ring-primary"
          />
          <p className="text-xs text-muted-foreground">
            Used for compliance notifications and audit communications
          </p>
        </div>

        {/* Save Button */}
        {!isReadOnly && (
          <div className="pt-2">
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
              ) : (
                <><Save className="mr-2 h-4 w-4" /> Save Changes</>
              )}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
